import { Dialog, DialogContent } from "@repo/ui/components/ui/dialog";
import { cn } from "@repo/ui/lib/utils";
import type { ReactNode } from "react";
import { ScheduleSessionsFooter } from "./footer";
import { ScheduleSessionsHeader } from "./header";
import { ScheduleSessionsProvider } from "./provider";
import { ReviewStep } from "./review-step";
import { SchedulingStep } from "./scheduling-step";
import { SessionSidebar } from "./session-sidebar";
import { useScheduleSessionsContext } from "./use-schedule-sessions-context";

/** Context-driven frame; callers compose the workflow pieces as children. */
function ScheduleSessionsFrame({ children, onExited }: { children: ReactNode; onExited: () => void }) {
  const { state, actions } = useScheduleSessionsContext();
  return (
    <Dialog
      open={state.open}
      onOpenChange={actions.setOpen}
      onOpenChangeComplete={(open) => {
        if (!open) onExited();
      }}
    >
      <DialogContent
        className="w-[calc(100vw-2rem)] gap-0 overflow-hidden p-0 sm:max-w-[1120px]"
        aria-describedby={undefined}
      >
        {/* Single child: the base DialogContent wraps children in a `grid gap-6`
            auto-height container, so a flat header/body/footer would gap apart
            and never bound their height. One flex column with a max-height gives
            a flush layout whose body scrolls internally. */}
        <div className="flex max-h-[85vh] flex-col overflow-hidden">{children}</div>
      </DialogContent>
    </Dialog>
  );
}

/** The persistent session sidebar plus the active step; review collapses the
 *  two scheduling columns into one. */
function ScheduleSessionsBody() {
  const { state } = useScheduleSessionsContext();
  return (
    <div
      className={cn(
        "grid min-h-0 flex-1 overflow-hidden",
        // Leave room for the editor timeline alongside the calendar and day rail.
        state.mode === "review" ? "grid-cols-[220px_1fr]" : "grid-cols-[220px_320px_minmax(0,1fr)]",
      )}
    >
      <SessionSidebar />
      {state.mode === "review" ? <ReviewStep /> : <SchedulingStep />}
    </div>
  );
}

export const ScheduleSessions = {
  Provider: ScheduleSessionsProvider,
  Frame: ScheduleSessionsFrame,
  Header: ScheduleSessionsHeader,
  Body: ScheduleSessionsBody,
  Footer: ScheduleSessionsFooter,
};
